/* eslint-disable no-await-in-loop */
import { openDB } from "idb";
import appVersion from "common/config/app-version.config";

export const IDB_NAME = "FYI";
const DEV_VERSION = 1;
const BUILD_SEPARATOR = "#";
const REVISION_SEPARATOR = ".";

export function getIDBVersion() {
  if (process.env.NODE_ENV === "production") {
    const [, buildVersion] = appVersion.split(BUILD_SEPARATOR);
    const [date, revision] = buildVersion.split(REVISION_SEPARATOR);
    const paddedRevision = revision.padStart(3, "0");

    return Number(`${date}${paddedRevision}`);
  }
  return DEV_VERSION;
}

export async function createIDBStores(stores) {
  let version;
  try {
    version = getIDBVersion();
  } catch (error) {
    return error;
  }

  try {
    await openDB(IDB_NAME, version, {
      upgrade(database) {
        // eslint-disable-next-line no-restricted-syntax
        stores.forEach(
          ({
            storeName,
            keyPath = null,
            autoIncrement = false,
            index = null,
          }) => {
            if (database.objectStoreNames.contains(storeName) === false) {
              const store = database.createObjectStore(storeName, {
                keyPath,
                autoIncrement,
              });

              if (index !== null) {
                store.createIndex(index.name, index.keyPath);
              }
            }
          },
        );
      },
    });
  } catch (error) {
    return error;
  }

  return null;
}

export async function putIDBValue(storeName, value, key = undefined) {
  const db = await openDB(IDB_NAME, getIDBVersion());
  const id = await db.put(storeName, value, key);

  return id;
}

export async function getIDBValue(storeName, key) {
  const db = await openDB(IDB_NAME, getIDBVersion());
  const value = await db.get(storeName, key);

  return value;
}

export async function deleteIDBValue(storeName, key) {
  const db = await openDB(IDB_NAME, getIDBVersion());
  await db.delete(storeName, key);
}

export async function clearIDBStore(storeName) {
  const db = await openDB(IDB_NAME, getIDBVersion());
  await db.clear(storeName);
}

export async function getCursor(
  storeName,
  query = undefined,
  transactionMode = undefined,
) {
  const db = await openDB(IDB_NAME, getIDBVersion());

  const cursor = await db
    .transaction(storeName, transactionMode)
    .store.openCursor(query);
  return cursor;
}

export async function getCursorFromIndex(
  storeName,
  indexName,
  query,
  transactionMode = undefined,
) {
  const db = await openDB(IDB_NAME, getIDBVersion());

  const index = await db
    .transaction(storeName, transactionMode)
    .store.index(indexName);
  const cursor = await index.openCursor(query);
  return cursor;
}

export async function getAllFromCursor(
  storeName,
  query = undefined,
  withKey = false,
) {
  let cursor = await getCursor(storeName, query);
  let result = [];

  while (cursor) {
    if (withKey === true) {
      result = result.concat({ key: cursor.key, value: cursor.value });
    } else {
      result = result.concat(cursor.value);
    }
    cursor = await cursor.continue();
  }

  return result;
}

export async function getAllFromIndex(storeName, indexName, query) {
  let cursor = await getCursorFromIndex(storeName, indexName, query);
  let result = [];

  while (cursor) {
    result = result.concat(cursor.value);
    cursor = await cursor.continue();
  }

  return result;
}

export async function deleteAllFromIndex(storeName, indexName, query) {
  let cursor = await getCursorFromIndex(
    storeName,
    indexName,
    query,
    "readwrite",
  );

  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
}

export async function idbKeyExists(storeName, key) {
  const db = await openDB(IDB_NAME, getIDBVersion());
  const count = await db.count(storeName, key);
  return count > 0;
}
